import { Injectable, Signal, computed, inject, signal, untracked } from '@angular/core';
import { paymentApi, paymentDtos, webApi, webDtos } from '@vectronic/openApi-admin';
import { getPaymentAccountLabel } from '@vectronic/util';
import { cloneDeep } from 'es-toolkit';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  filter,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { tap } from 'rxjs/operators';
import { LoggedInUserService } from './logged-in-user.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class PaymentAccountService {
  private paymentAccountApi = inject(paymentApi.PaymentAccount);
  private efficientAdminWebFetchApi = inject(webApi.AdminPayment);
  private userService = inject(UserService);
  private loggedInUserService = inject(LoggedInUserService);

  private readonly refresh$ = new BehaviorSubject<void>(null);

  getRefreshTrigger(): Observable<void> {
    return this.refresh$;
  }

  triggerRefresh() {
    this.refresh$.next(null);
  }

  readonly allPaymentAccounts$ = this.refresh$.pipe(
    switchMap(() => this.loggedInUserService.isLoaded$.pipe(filter(Boolean), take(1))),
    switchMap(() =>
      this.loggedInUserService.hasAccess(['AIRTIMES_READ', 'AIRTIMES_WRITE'])()
        ? this.paymentAccountApi.getPaymentAccounts({})
        : of([] as paymentDtos.PaymentAccount[]),
    ),
    catchError(() => of([] as paymentDtos.PaymentAccount[])),
    shareReplay(1),
  );

  private readonly allPaymentAccountsInternal = signal<paymentDtos.PaymentAccount[]>([]);
  private allPaymentAccountsSubscription?: Subscription;

  get allPaymentAccounts(): Signal<paymentDtos.PaymentAccount[]> {
    this.ensureAllPaymentAccountsLoaded();
    return this.allPaymentAccountsInternal;
  }

  private ensureAllPaymentAccountsLoaded() {
    if (this.allPaymentAccountsSubscription) {
      return;
    }
    this.allPaymentAccountsSubscription = untracked(() =>
      this.allPaymentAccounts$.subscribe((accounts) =>
        untracked(() => this.allPaymentAccountsInternal.set(accounts)),
      ),
    );
  }

  getPaymentAccountsByFilter(
    page?: number,
    count?: number,
    orderBy?: Array<string>,
    orderDirection?: string,
    fakturaCustomerRefNo?: Array<number>,
    user?: Array<number>,
    invoiceStatus?: 'OPEN' | 'OVERDUE',
    balanceStatus?: 'POSITIVE' | 'NEGATIVE' | 'LASTS_LESS_THAN_2_MONTHS',
    prePostStatus?: 'PRE' | 'POST',
    accountName?: string,
    fakturaCustomerRefNoSearch?: Array<number>,
    userSearch?: Array<number>,
    accountNameSearch?: Array<string>,
    internalCommentSearch?: Array<string>,
    customerRefNoAndAccountNumberSearch?: Array<string>,
    paymentAccountId?: Array<number>,
    active?: boolean,
    deviceIds?: number[],
    confirmed?: boolean,
  ): Observable<webDtos.PaymentAccountExtended[]> {
    return this.refresh$.pipe(
      switchMap(() =>
        this.efficientAdminWebFetchApi.getPaymentAccounts(page, count, orderBy, orderDirection, {
          fakturaCustomerRefNoFilter: fakturaCustomerRefNo,
          connectedUserAccountIdFilter: user,
          invoiceStatus: invoiceStatus,
          balanceStatus: balanceStatus,
          prePostStatus: prePostStatus,
          accountNameFilter: accountName,
          fakturaCustomerRefNoSearch: fakturaCustomerRefNoSearch,
          userSearch: userSearch,
          accountNameSearch: accountNameSearch,
          internalCommentSearch: internalCommentSearch,
          customerRefNoAndAccountNumberSearch: customerRefNoAndAccountNumberSearch,
          paymentAccountIdFilter: paymentAccountId,
          activeFilter: active,
          deviceIdsFilter: deviceIds,
          confirmedFilter: confirmed,
        }),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getPaymentAccountsCountByFilter(
    fakturaCustomerRefNo?: Array<number>,
    user?: Array<number>,
    invoiceStatus?: 'OPEN' | 'OVERDUE',
    balanceStatus?: 'POSITIVE' | 'NEGATIVE' | 'LASTS_LESS_THAN_2_MONTHS',
    prePostStatus?: 'PRE' | 'POST',
    accountName?: string,
    fakturaCustomerRefNoSearch?: Array<number>,
    userSearch?: Array<number>,
    accountNameSearch?: Array<string>,
    internalCommentSearch?: Array<string>,
    customerRefNoAndAccountNumberSearch?: Array<string>,
    paymentAccountId?: Array<number>,
    active?: boolean,
    deviceIds?: number[],
    confirmed?: boolean,
  ): Observable<number> {
    return this.refresh$.pipe(
      switchMap(() =>
        this.efficientAdminWebFetchApi.countPaymentAccounts({
          fakturaCustomerRefNoFilter: fakturaCustomerRefNo,
          connectedUserAccountIdFilter: user,
          invoiceStatus: invoiceStatus,
          balanceStatus: balanceStatus,
          prePostStatus: prePostStatus,
          accountNameFilter: accountName,
          fakturaCustomerRefNoSearch: fakturaCustomerRefNoSearch,
          userSearch: userSearch,
          accountNameSearch: accountNameSearch,
          internalCommentSearch: internalCommentSearch,
          customerRefNoAndAccountNumberSearch: customerRefNoAndAccountNumberSearch,
          paymentAccountIdFilter: paymentAccountId,
          activeFilter: active,
          deviceIdsFilter: deviceIds,
          confirmedFilter: confirmed,
        }),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  addPaymentAccount(paymentAccount: paymentDtos.PaymentAccountAdd) {
    return this.paymentAccountApi
      .addPaymentAccount(paymentAccount)
      .pipe(tap(() => this.refresh$.next(null)));
  }

  editPaymentAccount(id: number, paymentAccount: paymentDtos.PaymentAccountEdit) {
    const editData = cloneDeep(paymentAccount);
    delete editData['transactionsList'];
    delete editData['invoiceList'];
    delete editData['extendedUserPaymentAccountRoles'];
    delete editData['fakturaCustomer'];
    return this.paymentAccountApi
      .editPaymentAccount(id, editData)
      .pipe(tap(() => this.refresh$.next(null)));
  }

  deletePaymentAccount(id: number) {
    return this.paymentAccountApi
      .deletePaymentAccount(id)
      .pipe(tap(() => this.refresh$.next(null)));
  }

  confirmPaymentAccount(id: number) {
    return this.paymentAccountApi
      .confirmPaymentAccount(id)
      .pipe(tap(() => this.refresh$.next(null)));
  }

  readonly allPaymentAccountsWithExtendedRoles = computed<
    (paymentDtos.PaymentAccount & {
      extendedRoles: webDtos.UserPaymentAccountRoleExtended[];
      displayString: string;
    })[]
  >(() =>
    this.allPaymentAccounts().map((p) => {
      const extendedRoles = p.userPaymentAccountRoles?.map((r) => {
        const user = this.userService.users().find((u) => u.id === r.userAccountId);
        return {
          ...r,
          email: user?.email,
          name: user?.name,
        };
      });
      return {
        ...p,
        extendedRoles: extendedRoles,
        displayString: `${getPaymentAccountLabel(p)} - ${extendedRoles
          ?.filter((r) => r.role === 'OWNER')
          .map((r) => r.name)
          .join(', ')}`,
      };
    }),
  );
}
